import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';
import { EventImportReview } from './EventImportReview';

export const EventsModuleEditor = ({ data, onChange, siteId, sitePackage }) => {
  const events = data.events || [];
  const layout = data.layout || 'list';
  const spacing = data.spacing || 'spaced';
  const [editingIdx, setEditingIdx] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;
  const [imageUploading, setImageUploading] = useState(false);
  const [importDrafts, setImportDrafts] = useState(null);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const canUploadCsv = ['business', 'max'].includes(sitePackage);
  
  const addEvent = () => {
    const newEvent = { 
      id: Date.now().toString(),
      title: 'New Event', 
      venue: '', 
      location: '',
      date: '',
      time: '',
      ticket_url: '',
      show_tickets: true,
      more_info_url: '',
      show_more_info: false,
      description: '',
      image: '',
      is_past: false
    };
    onChange({
      ...data, 
      events: [...events, newEvent]
    });
    setEditingIdx(events.length);
  };
  
  const duplicateEvent = (idx) => {
    const eventToCopy = events[idx];
    const newEvent = {
      ...eventToCopy,
      id: Date.now().toString(),
      date: '',
      time: eventToCopy.time,
    };
    onChange({
      ...data,
      events: [...events, newEvent]
    });
    setEditingIdx(events.length);
  };
  
  const updateEvent = (idx, updates) => {
    const updated = [...events];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, events: updated});
  };
  
  const removeEvent = (idx) => {
    const deleted = events[idx];
    onChange({...data, events: events.filter((_, i) => i !== idx)});
    setEditingIdx(null);
    toast('Event deleted', {
      description: deleted?.title || deleted?.venue || undefined,
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          const cur = dataRef.current;
          const restored = [...(cur.events || [])];
          restored.splice(Math.min(idx, restored.length), 0, deleted);
          onChange({ ...cur, events: restored });
          toast.success('Event restored');
        },
      },
    });
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/ai/events/upload-csv/${siteId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      // Refresh events from response
      if (res.data.total_events) {
        // Re-fetch the site module to get updated events
        const siteRes = await api.get(`/sites/${siteId}`);
        const newEvents = siteRes.data?.modules?.events?.events || [];
        onChange({ ...data, events: newEvents });
      }
      if (res.data.errors?.length) {
        toast.warning(`${res.data.errors.length} rows had issues`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV upload failed');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (files.length > 6) {
      toast.error('Maximum 6 screenshots per import');
      e.target.value = '';
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      const res = await api.post(`/ai/events/upload-image/${siteId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      toast.success(`${res.data.message} (${res.data.credits_used} credits used, ${res.data.credits_remaining} left)`);
      setImportDrafts(res.data.drafts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI image import failed');
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmImport = async (approvedEvents) => {
    setConfirmingImport(true);
    try {
      const res = await api.post(`/ai/events/confirm/${siteId}`, { events: approvedEvents });
      toast.success(res.data.message);
      onChange({ ...data, events: res.data.events });
      setImportDrafts(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add events');
    } finally {
      setConfirmingImport(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Single event editor panel
  const renderEditor = (event, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Event</h4>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => duplicateEvent(idx)}
            title="Duplicate event"
            className="text-slate-400 hover:text-white hover:bg-slate-600"
          >
            <Copy className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setEditingIdx(null)}
            className="text-slate-400 hover:text-white"
          >
            Done
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Event Title / Show Name</Label>
        <Input 
          value={event.title} 
          onChange={(e) => updateEvent(idx, { title: e.target.value })}
          placeholder="e.g. Summer Tour 2026"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Venue</Label>
          <Input 
            value={event.venue} 
            onChange={(e) => updateEvent(idx, { venue: e.target.value })}
            placeholder="e.g. Royal Albert Hall"
          />
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input 
            value={event.location} 
            onChange={(e) => updateEvent(idx, { location: e.target.value })}
            placeholder="e.g. London, UK"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date</Label>
          <Input 
            type="date"
            value={event.date || ''} 
            onChange={(e) => updateEvent(idx, { date: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Time</Label>
          <Input 
            type="time"
            value={event.time || ''} 
            onChange={(e) => updateEvent(idx, { time: e.target.value })}
          />
        </div>
      </div>
      
      {/* Ticket Link with Toggle */}
      <div className="space-y-3 p-3 bg-slate-600/30 rounded-lg border border-slate-600/50">
        <div className="flex items-center justify-between">
          <Label className="font-medium text-slate-200">Ticket Button</Label>
          <Switch 
            checked={event.show_tickets !== false}
            onCheckedChange={(checked) => updateEvent(idx, { show_tickets: checked })}
          />
        </div>
        {event.show_tickets !== false && (
          <Input 
            value={event.ticket_url || ''} 
            onChange={(e) => updateEvent(idx, { ticket_url: e.target.value })}
            placeholder="https://tickets.example.com/..."
          />
        )}
      </div>
      
      {/* More Info Link with Toggle */}
      <div className="space-y-3 p-3 bg-slate-600/30 rounded-lg border border-slate-600/50">
        <div className="flex items-center justify-between">
          <Label className="font-medium text-slate-200">More Info Button</Label>
          <Switch 
            checked={event.show_more_info === true}
            onCheckedChange={(checked) => updateEvent(idx, { show_more_info: checked })}
          />
        </div>
        {event.show_more_info && (
          <Input 
            value={event.more_info_url || ''} 
            onChange={(e) => updateEvent(idx, { more_info_url: e.target.value })}
            placeholder="https://example.com/event-details"
          />
        )}
      </div>
      
      <div className="space-y-2">
        <Label>Description (Optional)</Label>
        <Textarea 
          value={event.description || ''} 
          onChange={(e) => updateEvent(idx, { description: e.target.value })}
          placeholder="Additional event details..."
          rows={2}
        />
      </div>

      <div className="space-y-2">
        <Label>Show Poster (Optional)</Label>
        <CloudinaryUploader
          currentImage={event.image || null}
          onUpload={(url) => updateEvent(idx, { image: url })}
          onRemove={() => updateEvent(idx, { image: '' })}
          folder="sites/events"
          aspectRatio="aspect-[2/3]"
          placeholder="Upload show poster"
          className="max-w-[180px]"
        />
      </div>

      <div className="flex items-center justify-between p-3 bg-slate-600/30 rounded-lg border border-slate-600/50">
        <div>
          <Label className="font-medium text-slate-200">Past production (archive)</Label>
          <p className="text-xs text-slate-400 mt-0.5">Shows in the Past Shows poster wall instead of upcoming — no date needed</p>
        </div>
        <Switch 
          checked={event.is_past === true}
          onCheckedChange={(checked) => updateEvent(idx, { is_past: checked })}
          data-testid="event-past-toggle"
        />
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => removeEvent(idx)} 
          className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete Event
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Tour Dates'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>
      
      {/* Layout Options */}
      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onChange({...data, layout: 'list'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'list' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="List view"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">List</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'grid'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'grid' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Card grid"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <span className="text-xs">Grid</span>
          </button>
          <button
            onClick={() => onChange({...data, layout: 'timeline'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              layout === 'timeline' 
                ? "bg-orange-500/20 border-orange-500 text-orange-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Timeline view"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Timeline</span>
          </button>
          
          <div className="h-8 w-px bg-slate-600 mx-2" />
          
          <button
            onClick={() => onChange({...data, spacing: 'spaced'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'spaced' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Spaced items"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4M14 6h4M4 12h4M14 12h4M4 18h4M14 18h4" />
            </svg>
            <span className="text-xs">Spaced</span>
          </button>
          <button
            onClick={() => onChange({...data, spacing: 'compact'})}
            className={cn(
              "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all",
              spacing === 'compact' 
                ? "bg-emerald-500/20 border-emerald-500 text-emerald-400" 
                : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
            )}
            title="Compact items"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">Compact</span>
          </button>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Switch 
            checked={data.past_events_visible === true}
            onCheckedChange={(checked) => onChange({...data, past_events_visible: checked})}
          />
          <Label>Show past shows on the site (poster archive / dimmed list)</Label>
        </div>
        {data.past_events_visible === true && (
          <div className="flex items-center gap-2 pl-14">
            <Label className="text-xs text-slate-400">Past shows view:</Label>
            <button
              onClick={() => onChange({...data, past_layout: 'wall'})}
              className={cn(
                "px-3 py-1.5 rounded-lg border text-xs transition-all",
                (data.past_layout || 'wall') === 'wall'
                  ? "bg-orange-500/20 border-orange-500 text-orange-400"
                  : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
              )}
              data-testid="past-layout-wall"
            >
              Poster Wall
            </button>
            <button
              onClick={() => onChange({...data, past_layout: 'carousel'})}
              className={cn(
                "px-3 py-1.5 rounded-lg border text-xs transition-all",
                data.past_layout === 'carousel'
                  ? "bg-orange-500/20 border-orange-500 text-orange-400"
                  : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
              )}
              data-testid="past-layout-carousel"
            >
              Carousel
            </button>
          </div>
        )}
      </div>

      {importDrafts && (
        <EventImportReview
          drafts={importDrafts}
          onCancel={() => setImportDrafts(null)}
          onConfirm={handleConfirmImport}
          saving={confirmingImport}
        />
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Events ({events.length})</Label>
          <div className="flex items-center gap-2">
            <label>
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
              <Button variant="outline" size="sm" asChild disabled={imageUploading} data-testid="image-upload-btn" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10">
                <span className="cursor-pointer">
                  <Image className="w-4 h-4 mr-1" /> {imageUploading ? 'AI reading image...' : 'AI Screenshot Import'}
                </span>
              </Button>
            </label>
            {canUploadCsv && (
              <label>
                <input type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" data-testid="csv-upload-input" />
                <Button variant="outline" size="sm" asChild disabled={csvUploading} data-testid="csv-upload-btn">
                  <span className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-1" /> {csvUploading ? 'Importing...' : 'CSV Import'}
                  </span>
                </Button>
              </label>
            )}
            <Button variant="outline" size="sm" onClick={addEvent} data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Event
            </Button>
          </div>
        </div>
        
        {events.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Calendar className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No events yet. Add your first show!</p>
          </div>
        ) : editingIdx !== null && events[editingIdx] ? (
          // Show editor for selected event
          renderEditor(events[editingIdx], editingIdx)
        ) : (
          // Show clean list view
          <div className="space-y-2">
            {events.map((event, idx) => (
              <div 
                key={event.id || idx} 
                onClick={() => setEditingIdx(idx)}
                className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group"
              >
                {/* Poster thumb or date badge */}
                {event.image ? (
                  <img src={event.image} alt="" className="w-12 h-16 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-12 rounded bg-orange-500/20 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-xs font-medium text-orange-400">{formatDate(event.date).split(' ')[1]}</span>
                    <span className="text-lg font-bold text-orange-400">{formatDate(event.date).split(' ')[0]}</span>
                  </div>
                )}
                
                {/* Event info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-white truncate">{event.title || 'Untitled Event'}</h4>
                    {(event.is_past === true || (event.date && new Date(event.date) < new Date(new Date().setHours(0,0,0,0)))) && (
                      <span className="text-[10px] font-semibold uppercase text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`past-badge-${idx}`}>
                        Past
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {event.venue}{event.location ? ` • ${event.location}` : ''}
                    {event.time ? ` • ${event.time}` : ''}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); duplicateEvent(idx); }}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setEditingIdx(idx); }}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); removeEvent(idx); }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Social Links Editor

